// Real-user INP diagnostics: for each interaction slower than 200 ms, reports which element was
// used, the timing phases and which scripts were running (Long Animation Frames API, Chromium only).
// No cookies or personal data. The endpoint comes from the script tag's data-endpoint attribute.
(() => {
  const script = document.currentScript;
  const endpoint = script?.dataset.endpoint;
  if (!endpoint || !('PerformanceObserver' in window)) return;

  const THRESHOLD = 200;
  const MAX_REPORTS_PER_PAGE = 10;
  const frames = [];
  const reported = new Set();
  let sent = 0;

  const selector = (el) => {
    const parts = [];
    for (let node = el; node && node.nodeType === 1 && parts.length < 4; node = node.parentElement) {
      let part = node.tagName.toLowerCase();
      if (node.id) {
        parts.unshift(`#${node.id}`);
        break;
      }
      const classes = [...node.classList].filter((c) => !/^(is-|js-)/.test(c)).slice(0, 2);
      if (classes.length) part += `.${classes.join('.')}`;
      parts.unshift(part);
    }
    return parts.join('>');
  };

  const scriptLabel = (url) => {
    if (!url) return 'inline/tema';
    if (url.includes('/cdn/wpm/')) return 'shopify-pixels';
    if (url.includes('sst.onewatchcompany.com') || url.includes('googletagmanager')) return 'gtm';
    if (url.includes('clarity')) return 'clarity';
    const theme = url.match(/\/cdn\/shop\/t\/\d+\/assets\/([^?]+)/);
    if (theme) return `tema:${theme[1]}`;
    try {
      return new URL(url).hostname;
    } catch {
      return 'outro';
    }
  };

  const browser = () => {
    const ua = navigator.userAgent;
    if (/Instagram/.test(ua)) return 'instagram';
    if (/FBAN|FBAV/.test(ua)) return 'facebook';
    if (/CriOS|Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'chrome';
    if (/Safari\//.test(ua)) return 'safari';
    if (/Firefox\//.test(ua)) return 'firefox';
    return 'outro';
  };

  const report = (entry) => {
    if (sent >= MAX_REPORTS_PER_PAGE || reported.has(entry.interactionId)) return;
    reported.add(entry.interactionId);
    sent++;

    const start = entry.startTime;
    const end = start + entry.duration;
    const byScript = {};
    for (const frame of frames) {
      if (frame.startTime + frame.duration < start || frame.startTime > end) continue;
      for (const s of frame.scripts || []) {
        const key = scriptLabel(s.sourceURL);
        byScript[key] = (byScript[key] || 0) + s.duration;
      }
    }
    const scripts = Object.entries(byScript)
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => `${k}:${Math.round(v)}`)
      .join(', ');

    const payload = JSON.stringify({
      page: location.pathname,
      pageType: window.Theme?.template?.name || '',
      el: entry.target ? selector(entry.target) : '',
      ev: entry.name,
      inp: Math.round(entry.duration),
      input: Math.round(entry.processingStart - entry.startTime),
      proc: Math.round(entry.processingEnd - entry.processingStart),
      pres: Math.round(end - entry.processingEnd),
      scripts,
      device: window.innerWidth < 750 ? 'mobile' : 'desktop',
      net: navigator.connection?.effectiveType || '',
      browser: browser(),
    });
    navigator.sendBeacon(endpoint, new Blob([payload], { type: 'text/plain' }));
  };

  try {
    new PerformanceObserver((list) => {
      for (const f of list.getEntries()) frames.push(f);
      if (frames.length > 200) frames.splice(0, frames.length - 200);
    }).observe({ type: 'long-animation-frame', buffered: true });
  } catch {
    // Not supported (Safari/Firefox): timings are still reported, without script attribution
  }

  try {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.interactionId && entry.duration >= THRESHOLD) {
          // Wait for the frames around this interaction to be recorded
          setTimeout(() => report(entry), 1000);
        }
      }
    }).observe({ type: 'event', durationThreshold: 104, buffered: true });
  } catch {
    // Event Timing not supported
  }
})();
